import { Component, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { GlobalService } from 'src/app/app-core/lib/global.service';
import { ProcessService } from 'src/app/app-core/services/process.service';
import { DocumentSignatureService } from 'src/app/app-core/services/document-signature.service';
import { environment } from 'src/environments/environment';
import { SignedFileDataResponse } from 'src/app/interfaces/signature-gov.interfaces';
import {
    ICertidaoPayload,
    ICertidaoOrgao,
    ICertidaoDepartamento,
    ICertidaoSecretario,
} from 'src/app/interfaces/certidao.interface';

type SignaturePosition = { page: number; xPercentage: number; yPercentage: number };

/**
 * Certidão Digital da SEMURB — processos externos.
 *
 * Espelha o fluxo do Parecer de Despacho, com duas diferenças: o formulário é
 * dividido em 4 steps e, quando a assinatura do secretário é obrigatória, a
 * assinatura do responsável passa a ser opcional (botão "Enviar ao Secretário").
 */
@Component({
    selector: 'app-create-certidao-dialog',
    templateUrl: './create-certidao-dialog.component.html',
    styleUrls: ['./create-certidao-dialog.component.scss'],
    providers: [MessageService, ProcessService, DocumentSignatureService],
})
export class CreateCertidaoDialogComponent implements OnChanges, OnDestroy {
    @Input() process: any;
    @Input() isModalVisible: boolean = false;
    @Input() existingDraft: any = null;

    @Output() closed = new EventEmitter<void>();
    @Output() saved = new EventEmitter<any>();

    public readonly totalSteps = 4;
    public currentStep = 1;

    public certidaoId: number | null = null;

    // Step 1 — dados da licença
    public assunto = '';
    public numeroLicenca = '';
    public validadeLicenca = '';

    // Step 2 — dados do interessado
    public nomeRazaoSocial = '';
    public cpfCnpj = '';
    public atividadePrincipal = '';
    public endereco = '';
    public municipio = '';
    public bairro = '';
    public uf = '';
    public cep = '';
    public telefone = '';
    public email = '';
    public numeroProcesso = '';
    public empreendimento = '';

    // Step 3 — textos
    public objeto = '';
    public condicionantes = '';
    public condicionantesPrazo = '';

    // Step 4 — assinatura
    public secretarySignatureRequired = false;
    public orgaos: ICertidaoOrgao[] = [];
    public departamentos: ICertidaoDepartamento[] = [];
    public secretarios: ICertidaoSecretario[] = [];
    public selectedOrgao: number | null = null;
    public selectedDepartamento: number | null = null;
    public selectedSecretario: number | null = null;
    public isLoadingOrgaos = false;
    public isLoadingDepartamentos = false;
    public isLoadingSecretarios = false;

    public isSaving = false;
    public isSigning = false;
    public isPreviewing = false;
    public isSendingToSecretary = false;
    public signingMessage = '';

    public editorConfig = {
        height: 260,
        base_url: '/tinymce',
        suffix: '.min',
        menubar: false,
        statusbar: false,
        toolbar_mode: 'wrap',
        plugins:
            'preview importcss searchreplace autolink directionality visualblocks visualchars fullscreen link table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help charmap quickbars',
        toolbar:
            'undo redo | blocks | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | outdent indent | table | numlist bullist | forecolor backcolor removeformat | link | charmap | fullscreen preview',
        table_toolbar:
            'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tablemergecells tablesplitcells',
        table_default_attributes: { border: '1' },
        table_default_styles: { 'border-collapse': 'collapse', width: '100%' },
        fontsize_formats: '8pt 10pt 12pt 14pt 18pt 24pt 36pt',
        wordcount_display: false,
        language_url: '/assets/languages/pt_BR.js',
        language: 'pt_BR',
        content_style:
            'body { font-family:Inter, sans-serif; font-size:14px; } table { border-collapse: collapse; } table td, table th { border: 1px solid #999; padding: 4px 8px; }',
        setup: (editor: any) => {
            const getBlock = (node: any) => editor.dom.getParent(node, 'p,div,h1,h2,h3,h4,h5,h6');

            editor.on('keydown', (event: KeyboardEvent) => {
                if (event.key === 'Tab') {
                    const node = editor.selection.getNode();
                    const block = getBlock(node);

                    if (!block || editor.dom.getParent(node, 'li,td,th')) return;

                    event.preventDefault();
                    editor.dom.setStyle(block, 'text-indent', event.shiftKey ? '' : '4em');
                    return;
                }

                if (event.key === 'Enter' && event.shiftKey) {
                    const before = getBlock(editor.selection.getNode());
                    setTimeout(() => {
                        const after = getBlock(editor.selection.getNode());
                        if (after && after !== before) {
                            editor.dom.setStyle(after, 'text-indent', '');
                        }
                    });
                }
            });
        },
    };

    private destroy$ = new Subject<void>();
    private popupPollInterval: any = null;
    private pendingSignContext: {
        pdfBase64: string;
        fileName: string;
        pageCount: number;
        signaturePosition: SignaturePosition | null;
        secretarioSignaturePosition: SignaturePosition | null;
    } | null = null;
    private secretarioSignaturePosition: SignaturePosition | null = null;
    private boundMessageListener: ((event: MessageEvent) => void) | null = null;

    constructor(
        private global: GlobalService,
        private processService: ProcessService,
        private documentSignatureService: DocumentSignatureService,
        private ngZone: NgZone,
        private messageService: MessageService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isModalVisible && this.isModalVisible) {
            this.currentStep = 1;
            this.hydrateFromDraft();
            if (!this.existingDraft) {
                this.loadPrefill();
            }
            if (this.secretarySignatureRequired) {
                this.loadOrgaos();
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.clearPopupPoll();
    }

    get processNumber(): string {
        return this.process?.PRC_NumeroProcesso || '';
    }

    get isBusy(): boolean {
        return this.isSaving || this.isSigning || this.isPreviewing || this.isSendingToSecretary;
    }

    get stepLabels(): string[] {
        return ['Dados da licença', 'Dados do interessado', 'Textos', 'Assinatura'];
    }

    // ------------------------------------------------------------------
    // Navegação entre steps
    // ------------------------------------------------------------------

    goToStep(step: number): void {
        if (step < 1 || step > this.totalSteps) return;
        if (step > this.currentStep && !this.validateUpTo(step - 1)) return;
        this.currentStep = step;
    }

    nextStep(): void {
        if (!this.validateStep(this.currentStep)) return;
        if (this.currentStep < this.totalSteps) this.currentStep += 1;
    }

    previousStep(): void {
        if (this.currentStep > 1) this.currentStep -= 1;
    }

    private validateUpTo(step: number): boolean {
        for (let i = 1; i <= step; i++) {
            if (!this.validateStep(i)) {
                this.currentStep = i;
                return false;
            }
        }
        return true;
    }

    private warn(detail: string): boolean {
        this.messageService.add({ severity: 'warn', summary: 'Atenção', detail });
        return false;
    }

    private validateStep(step: number): boolean {
        if (step === 1) {
            if (!this.assunto.trim()) return this.warn('Informe o assunto da certidão.');
            if (!this.numeroLicenca.trim()) return this.warn('Informe o número da licença.');
            if (!this.validadeLicenca) return this.warn('Informe a validade da licença.');
            return true;
        }

        if (step === 2) {
            if (!this.nomeRazaoSocial.trim()) return this.warn('O nome/razão social do interessado é obrigatório.');

            const obrigatorios: Array<[string, string]> = [
                [this.atividadePrincipal, 'a atividade principal (03)'],
                [this.endereco, 'o endereço (04)'],
                [this.municipio, 'o município (05)'],
                [this.bairro, 'o distrito/localidade/bairro (06)'],
                [this.uf, 'a UF (07)'],
                [this.cep, 'o CEP (08)'],
                [this.telefone, 'o telefone (09)'],
                [this.email, 'o e-mail (10)'],
                [this.empreendimento, 'o empreendimento (12)'],
            ];

            for (const [valor, rotulo] of obrigatorios) {
                if (!(valor || '').trim()) return this.warn(`Informe ${rotulo}.`);
            }

            if (!/^[A-Za-z]{2}$/.test(this.uf.trim())) {
                return this.warn('A UF (07) deve ter duas letras. Ex.: CE.');
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim())) {
                return this.warn('Informe um e-mail (10) válido.');
            }

            return true;
        }

        if (step === 3) {
            if (!this.stripHtml(this.objeto)) return this.warn('Preencha o objeto da certidão.');
            return true;
        }

        if (step === 4) {
            if (this.secretarySignatureRequired && !this.selectedOrgao) {
                return this.warn('Selecione o órgão do(a) secretário(a) responsável pela assinatura.');
            }
            if (this.secretarySignatureRequired && !this.selectedSecretario) {
                return this.warn('Selecione o(a) secretário(a) responsável pela assinatura.');
            }
            return true;
        }

        return true;
    }

    private stripHtml(html: string): string {
        return (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    }

    private validateAll(): boolean {
        return this.validateUpTo(this.totalSteps);
    }

    // ------------------------------------------------------------------
    // Carregamento de dados
    // ------------------------------------------------------------------

    private loadPrefill(): void {
        if (!this.process?.PRC_CodigoProcesso) return;

        this.processService
            .getCertidaoPrefill(this.process.PRC_CodigoProcesso)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (prefill) => {
                    this.nomeRazaoSocial = prefill.nomeRazaoSocial || '';
                    this.cpfCnpj = prefill.cpfCnpj || '';
                    this.numeroProcesso = prefill.numeroProcesso || '';
                    this.endereco = prefill.endereco || '';
                    this.municipio = prefill.municipio || '';
                    this.bairro = prefill.bairro || '';
                    this.uf = prefill.uf || '';
                    this.cep = prefill.cep || '';
                    this.telefone = prefill.telefone || '';
                    this.email = prefill.email || '';
                },
                error: () => {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Atenção',
                        detail: 'Não foi possível carregar os dados do solicitante.',
                    });
                },
            });
    }

    onSecretaryCheckboxChange(): void {
        if (this.secretarySignatureRequired) {
            this.loadOrgaos();
        } else {
            this.orgaos = [];
            this.departamentos = [];
            this.secretarios = [];
            this.selectedOrgao = null;
            this.selectedDepartamento = null;
            this.selectedSecretario = null;
        }
    }

    /**
     * O órgão do processo entra apenas como sugestão inicial — o(a) secretário(a)
     * que assina pode estar lotado(a) em outro órgão.
     */
    private loadOrgaos(): void {
        if (!this.process?.PRC_CodigoProcesso) return;

        this.isLoadingOrgaos = true;
        this.processService
            .getCertidaoOrgaos(this.process.PRC_CodigoProcesso)
            .pipe(takeUntil(this.destroy$), finalize(() => (this.isLoadingOrgaos = false)))
            .subscribe({
                next: ({ orgaos, orgaoProcesso }) => {
                    this.orgaos = (orgaos || []).map((orgao) => ({
                        ...orgao,
                        OR_Rotulo: [orgao.OR_Sigla, orgao.OR_Descricao].filter((part) => !!part).join(' — '),
                    }));

                    const disponivel = (codigo: number | null) =>
                        !!codigo && this.orgaos.some((o) => o.OR_CodigoOrgao === codigo);

                    if (!disponivel(this.selectedOrgao)) {
                        this.selectedOrgao = disponivel(orgaoProcesso) ? orgaoProcesso : null;
                    }

                    if (this.selectedOrgao) {
                        this.loadDepartamentos();
                    }
                },
                error: () => {
                    this.orgaos = [];
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Atenção',
                        detail: 'Não foi possível carregar os órgãos com secretários cadastrados.',
                    });
                },
            });
    }

    onOrgaoChange(): void {
        this.selectedDepartamento = null;
        this.selectedSecretario = null;
        this.departamentos = [];
        this.secretarios = [];
        if (this.selectedOrgao) {
            this.loadDepartamentos();
        }
    }

    private loadDepartamentos(): void {
        if (!this.process?.PRC_CodigoProcesso || !this.selectedOrgao) return;

        this.isLoadingDepartamentos = true;
        this.processService
            .getCertidaoDepartamentos(this.process.PRC_CodigoProcesso, this.selectedOrgao)
            .pipe(takeUntil(this.destroy$), finalize(() => (this.isLoadingDepartamentos = false)))
            .subscribe({
                next: ({ departamentos }) => {
                    this.departamentos = departamentos || [];
                    if (this.departamentos.length === 1) {
                        this.selectedDepartamento = this.departamentos[0].DEP_CodigoDepartamento;
                    } else if (
                        this.selectedDepartamento &&
                        !this.departamentos.some((d) => d.DEP_CodigoDepartamento === this.selectedDepartamento)
                    ) {
                        this.selectedDepartamento = null;
                        this.selectedSecretario = null;
                    }
                    if (this.selectedDepartamento) {
                        this.loadSecretarios();
                    }
                },
                error: () => {
                    this.departamentos = [];
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Atenção',
                        detail: 'Não foi possível carregar os departamentos da secretaria.',
                    });
                },
            });
    }

    onDepartamentoChange(): void {
        this.selectedSecretario = null;
        this.secretarios = [];
        if (this.selectedDepartamento) {
            this.loadSecretarios();
        }
    }

    private loadSecretarios(): void {
        if (!this.process?.PRC_CodigoProcesso || !this.selectedDepartamento) return;

        this.isLoadingSecretarios = true;
        this.processService
            .getCertidaoSecretarios(
                this.process.PRC_CodigoProcesso,
                this.selectedDepartamento,
                this.selectedOrgao ?? undefined,
            )
            .pipe(takeUntil(this.destroy$), finalize(() => (this.isLoadingSecretarios = false)))
            .subscribe({
                next: ({ secretarios }) => {
                    this.secretarios = secretarios || [];
                    if (this.secretarios.length === 1) {
                        this.selectedSecretario = this.secretarios[0].PE_CodigoPessoa;
                    } else if (
                        this.selectedSecretario &&
                        !this.secretarios.some((s) => s.PE_CodigoPessoa === this.selectedSecretario)
                    ) {
                        this.selectedSecretario = null;
                    }
                },
                error: () => {
                    this.secretarios = [];
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Atenção',
                        detail: 'Não foi possível carregar os secretários do departamento.',
                    });
                },
            });
    }

    // ------------------------------------------------------------------
    // Persistência
    // ------------------------------------------------------------------

    private buildPayload(): ICertidaoPayload {
        return {
            assunto: this.assunto,
            numeroLicenca: this.numeroLicenca,
            validadeLicenca: this.validadeLicenca || null,
            nomeRazaoSocial: this.nomeRazaoSocial,
            cpfCnpj: this.cpfCnpj,
            atividadePrincipal: this.atividadePrincipal,
            endereco: this.endereco,
            municipio: this.municipio,
            bairro: this.bairro,
            uf: this.uf,
            cep: this.cep,
            telefone: this.telefone,
            email: this.email,
            numeroProcesso: this.numeroProcesso,
            empreendimento: this.empreendimento,
            objeto: this.objeto,
            condicionantes: this.condicionantes,
            condicionantesPrazo: this.condicionantesPrazo,
            assinaturaSecretarioObrigatoria: this.secretarySignatureRequired,
            codigoOrgaoSecretario: this.secretarySignatureRequired ? this.selectedOrgao : null,
            codigoDepartamentoSecretario: this.secretarySignatureRequired ? this.selectedDepartamento : null,
            codigoPessoaSecretario: this.secretarySignatureRequired ? this.selectedSecretario : null,
        };
    }

    private persistDraft() {
        this.isSaving = true;
        const userId = this.global.user()?.PE_CodigoPessoa;
        const payload = this.buildPayload();

        const request$ = this.certidaoId
            ? this.processService.updateCertidaoDraft(this.process.PRC_CodigoProcesso, this.certidaoId, payload)
            : this.processService.createCertidaoDraft(this.process.PRC_CodigoProcesso, { ...payload, userId });

        return request$.pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isSaving = false)),
        );
    }

    onSaveDraftClick(): void {
        if (!this.validateStep(1)) return;

        this.persistDraft().subscribe({
            next: (certidao) => {
                this.certidaoId = certidao.PC_CodigoCertidao;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Rascunho salvo',
                    detail: 'A certidão foi salva como rascunho.',
                });
                this.saved.emit(certidao);
            },
            error: (err) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: err?.error?.message || 'Não foi possível salvar o rascunho.',
                });
            },
        });
    }

    onPreviewClick(): void {
        if (!this.validateUpTo(3)) return;

        this.isPreviewing = true;
        const user = this.global.user();

        this.processService
            .previewCertidao(this.process.PRC_CodigoProcesso, {
                ...this.buildPayload(),
                userId: user?.USR_CodigoUsuario ?? user?.PE_CodigoPessoa,
            })
            .pipe(takeUntil(this.destroy$), finalize(() => (this.isPreviewing = false)))
            .subscribe({
                next: (blob) => {
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Não foi possível gerar a pré-visualização.',
                    });
                },
            });
    }

    /**
     * "Enviar ao Secretário": o responsável encaminha sem assinar. Só fica
     * disponível quando a assinatura do secretário é obrigatória.
     */
    onSendToSecretaryClick(): void {
        if (!this.validateAll()) return;
        if (!this.secretarySignatureRequired) return;

        this.isSendingToSecretary = true;
        this.persistDraft().subscribe({
            next: (certidao) => {
                this.certidaoId = certidao.PC_CodigoCertidao;
                this.processService
                    .sendCertidaoToSecretary(this.process.PRC_CodigoProcesso, this.certidaoId!)
                    .pipe(takeUntil(this.destroy$), finalize(() => (this.isSendingToSecretary = false)))
                    .subscribe({
                        next: (updated) => {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Enviado',
                                detail: 'A certidão foi encaminhada para assinatura do(a) secretário(a). ' +
                                        'O processo aguarda essa assinatura para seguir.',
                            });
                            this.resetState();
                            this.closed.emit();
                            setTimeout(() => this.saved.emit(updated));
                        },
                        error: (err) => {
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Erro',
                                detail: err?.error?.message || 'Não foi possível enviar ao(à) secretário(a).',
                            });
                        },
                    });
            },
            error: (err) => {
                this.isSendingToSecretary = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: err?.error?.message || 'Não foi possível salvar a certidão antes do envio.',
                });
            },
        });
    }

    // ------------------------------------------------------------------
    // Assinatura gov.br
    // ------------------------------------------------------------------

    onSignClick(): void {
        if (!this.validateAll()) return;

        this.isSigning = true;
        this.signingMessage = 'Salvando rascunho...';
        this.persistDraft().subscribe({
            next: (certidao) => {
                this.certidaoId = certidao.PC_CodigoCertidao;
                this.signingMessage = 'Gerando PDF para assinatura...';
                this.preparePdfAndOpenGovBr();
            },
            error: (err) => {
                this.endSigningFlow();
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: err?.error?.message || 'Não foi possível preparar a certidão para assinatura.',
                });
            },
        });
    }

    private endSigningFlow(): void {
        this.isSigning = false;
        this.signingMessage = '';
    }

    private preparePdfAndOpenGovBr(): void {
        if (!this.certidaoId) {
            this.endSigningFlow();
            return;
        }

        this.processService
            .prepareCertidaoSign(this.process.PRC_CodigoProcesso, this.certidaoId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    const { pdfBase64, fileName, pageCount, signaturePosition, secretarioSignaturePosition } = response;
                    this.secretarioSignaturePosition = secretarioSignaturePosition || null;
                    this.signingMessage = 'Aguardando assinatura no gov.br...';
                    this.openGovBrPopup(pdfBase64, fileName, pageCount, signaturePosition, secretarioSignaturePosition);
                },
                error: (err) => {
                    this.endSigningFlow();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: err?.error?.message || 'Falha ao gerar PDF para assinatura.',
                    });
                },
            });
    }

    private openGovBrPopup(
        pdfBase64: string,
        fileName: string,
        pageCount: number,
        signaturePosition: SignaturePosition | null,
        secretarioSignaturePosition: SignaturePosition | null,
    ): void {
        const signUrl = `${environment.govBR.signUrl}/authorize?response_type=code&redirect_uri=${encodeURIComponent(
            environment.govBR.returnSignUrl,
        )}&scope=sign&client_id=${environment.govBR.signClientID}`;
        const width = 800;
        const height = 600;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(signUrl, '_blank', `width=${width},height=${height},top=${top},left=${left}`);

        this.pendingSignContext = {
            pdfBase64,
            fileName,
            pageCount,
            signaturePosition,
            secretarioSignaturePosition,
        };
        this.registerPostMessageFallback();

        const POLL_TIMEOUT_MS = 5 * 60 * 1000;
        const startedAt = Date.now();

        this.popupPollInterval = setInterval(() => {
            if (!popup || popup.closed) {
                this.clearPopupPoll();
                this.ngZone.run(() => {
                    if (this.pendingSignContext) {
                        this.pendingSignContext = null;
                        this.endSigningFlow();
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Assinatura cancelada',
                            detail: 'A janela de assinatura foi fechada antes da conclusão.',
                        });
                    }
                });
                return;
            }

            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                try { popup.close(); } catch { /* noop */ }
                this.clearPopupPoll();
                this.ngZone.run(() => {
                    this.pendingSignContext = null;
                    this.endSigningFlow();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Tempo esgotado',
                        detail: 'A assinatura não foi concluída a tempo. Tente novamente.',
                    });
                });
                return;
            }

            try {
                if (popup.location.href.includes('code=')) {
                    const url = new URL(popup.location.href);
                    const code = url.searchParams.get('code');
                    popup.close();
                    this.clearPopupPoll();
                    this.ngZone.run(() => {
                        if (code && this.pendingSignContext) {
                            const ctx = this.pendingSignContext;
                            this.pendingSignContext = null;
                            this.callSignApi(code, ctx.pdfBase64, ctx.fileName, ctx.pageCount, ctx.signaturePosition);
                        } else {
                            this.endSigningFlow();
                        }
                    });
                }
            } catch {
                // Cross-origin enquanto o popup está no domínio gov.br
            }
        }, 1000);
    }

    private registerPostMessageFallback(): void {
        if (this.boundMessageListener) return;

        this.boundMessageListener = (event: MessageEvent) => {
            try {
                const returnUrlOrigin = new URL(environment.govBR.returnSignUrl).origin;
                if (event.origin !== returnUrlOrigin) return;
                const code = (event.data && (event.data.code || event.data.signCode)) as string | undefined;
                if (!code || !this.pendingSignContext) return;
                const ctx = this.pendingSignContext;
                this.pendingSignContext = null;
                this.clearPopupPoll();
                this.ngZone.run(() => this.callSignApi(code, ctx.pdfBase64, ctx.fileName, ctx.pageCount, ctx.signaturePosition));
            } catch {
                /* noop */
            }
        };
        window.addEventListener('message', this.boundMessageListener);
    }

    private callSignApi(
        code: string,
        pdfBase64: string,
        fileName: string,
        pageCount: number,
        computedPosition: SignaturePosition | null,
    ): void {
        const lastPageIndex = Math.max(0, (pageCount || 1) - 1);
        const signaturePosition = computedPosition || { page: lastPageIndex, xPercentage: 50, yPercentage: 15 };
        this.signingMessage = 'Assinando documento...';

        this.documentSignatureService
            .signatureAndSaveDocument({ code, fileName, pdfBase64, signaturePosition })
            .subscribe({
                next: (response: SignedFileDataResponse) => {
                    this.signingMessage = 'Finalizando assinatura...';
                    this.finalizeSign(response.fileCloudFlare, fileName);
                },
                error: () => {
                    this.endSigningFlow();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha na assinatura via gov.br.',
                    });
                },
            });
    }

    private finalizeSign(fileCloudFlare: string, fileName: string): void {
        if (!this.certidaoId) return;

        this.processService
            .finalizeCertidaoSign(this.process.PRC_CodigoProcesso, this.certidaoId, {
                fileCloudFlare,
                fileName: `${fileName}.pdf`,
                secretarioSignaturePosition: this.secretarioSignaturePosition,
            })
            .pipe(takeUntil(this.destroy$), finalize(() => this.endSigningFlow()))
            .subscribe({
                next: (certidao) => {
                    const signedCertidao = { ...certidao, __signedJustNow: true };
                    this.resetState();
                    this.closed.emit();
                    setTimeout(() => this.saved.emit(signedCertidao));
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: err?.error?.message || 'Não foi possível finalizar a assinatura.',
                    });
                },
            });
    }

    // ------------------------------------------------------------------
    // Estado
    // ------------------------------------------------------------------

    onModalHide(): void {
        this.resetState();
        this.closed.emit();
    }

    private toDateInputValue(raw: any): string {
        if (!raw) return '';
        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) return '';
        return date.toISOString().substring(0, 10);
    }

    private hydrateFromDraft(): void {
        const d = this.existingDraft;

        this.certidaoId = d ? d.PC_CodigoCertidao : null;
        this.assunto = d?.PC_Assunto || '';
        this.numeroLicenca = d?.PC_NumeroLicenca || '';
        this.validadeLicenca = this.toDateInputValue(d?.PC_ValidadeLicenca);
        this.nomeRazaoSocial = d?.PC_NomeRazaoSocial || '';
        this.cpfCnpj = d?.PC_CpfCnpj || '';
        this.atividadePrincipal = d?.PC_AtividadePrincipal || '';
        this.endereco = d?.PC_Endereco || '';
        this.municipio = d?.PC_Municipio || '';
        this.bairro = d?.PC_Bairro || '';
        this.uf = d?.PC_UF || '';
        this.cep = d?.PC_CEP || '';
        this.telefone = d?.PC_Telefone || '';
        this.email = d?.PC_Email || '';
        this.numeroProcesso = d?.PC_NumeroProcesso || '';
        this.empreendimento = d?.PC_Empreendimento || '';
        this.objeto = d?.PC_Objeto || '';
        this.condicionantes = d?.PC_Condicionantes || '';
        this.condicionantesPrazo = d?.PC_CondicionantesPrazo || '';
        this.secretarySignatureRequired = !!d?.PC_AssinaturaSecretarioObrigatoria;
        this.selectedOrgao = d?.PC_CodigoOrgaoSecretario ?? null;
        this.selectedDepartamento = d?.PC_CodigoDepartamentoSecretario ?? null;
        this.selectedSecretario = d?.PC_CodigoPessoaSecretario ?? null;

        this.orgaos = [];
        this.departamentos = [];
        this.secretarios = [];
    }

    private resetState(): void {
        this.existingDraft = null;
        this.hydrateFromDraft();
        this.currentStep = 1;
        this.isSaving = false;
        this.isSigning = false;
        this.isPreviewing = false;
        this.isSendingToSecretary = false;
        this.signingMessage = '';
        this.clearPopupPoll();
    }

    private clearPopupPoll(): void {
        if (this.popupPollInterval) {
            clearInterval(this.popupPollInterval);
            this.popupPollInterval = null;
        }
        if (this.boundMessageListener) {
            window.removeEventListener('message', this.boundMessageListener);
            this.boundMessageListener = null;
        }
    }
}
